/**
 * Self profile edit – display name, avatar, description, phone, skill level (per game), years of experience (per game).
 * No role editing; only super admins can change roles (via Admin panel).
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGame } from '../contexts/GameContext';
import { doc, addDoc, collection, getDoc, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { userUpdateProfile } from '../api/userApi';
import * as readApi from '../api/readApi';
import { logAudit, AUDIT_ACTIONS, AUDIT_ENTITIES } from '../services/auditLogService';
import { saveUserToAlgolia } from '../services/userAlgoliaService';
import EntityProfileFields, { getEntityProfileDefaults } from '../components/EntityProfileFields';
import { getProfileForGame } from '../utils/gameProfile';
import { TIMEZONE_OPTIONS, DEFAULT_TIMEZONE } from '../utils/dateTimeUtils';
import { useAlert } from '../contexts/AlertContext';
import { logUserActivity } from '../services/auditLogService';
import './ProfileEdit.css';

export default function ProfileEdit() {
  const { showAlert, showToast } = useAlert();
  const { currentUser, userProfile, refreshUserProfile } = useAuth();
  const { gameId, currentGame } = useGame();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [skillLevel, setSkillLevel] = useState('average');
  const [yearsOfExperience, setYearsOfExperience] = useState('');
  const [profileFields, setProfileFields] = useState({ avatarURL: '', description: '', phone: '', address: '', mapUrl: '' });
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [saving, setSaving] = useState(false);
  const [activeTournament, setActiveTournament] = useState(null);
  const [celebrationSuppressSaving, setCelebrationSuppressSaving] = useState(false);

  const gameProfile = getProfileForGame(userProfile, gameId);
  const suppressedTournaments = userProfile?.leaderboardCelebrationSuppressed || {};
  const gameLabel = currentGame?.name || 'this game';

  useEffect(() => {
    if (!userProfile && !currentUser) return;
    setDisplayName((userProfile?.displayName || currentUser?.displayName || currentUser?.email || '').trim());
    setSkillLevel(gameProfile.skillLevel || 'average');
    setYearsOfExperience(String(gameProfile.yearsOfExperience ?? ''));
    setProfileFields(getEntityProfileDefaults(userProfile));
    setTimezone(userProfile?.timezone || DEFAULT_TIMEZONE);
  }, [userProfile, currentUser, gameId, gameProfile.skillLevel, gameProfile.yearsOfExperience]);

  useEffect(() => {
    if (!currentUser?.uid || !gameId) return;
    let cancelled = false;
    readApi.getDashboardData(gameId).then((data) => {
      if (!cancelled && data?.isRegistered && data?.activeTournament) {
        setActiveTournament(data.activeTournament);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [currentUser?.uid, gameId]);

  async function toggleCelebrationSuppress(tournamentId, checked) {
    if (!currentUser?.uid || !tournamentId) return;
    setCelebrationSuppressSaving(true);
    try {
      const next = { ...suppressedTournaments };
      if (checked) next[tournamentId] = true;
      else delete next[tournamentId];
      await userUpdateProfile(currentUser.uid, {
        leaderboardCelebrationSuppressed: Object.keys(next).length ? next : {},
      });
      await refreshUserProfile();
      logUserActivity(
        { userId: currentUser.uid, userEmail: currentUser.email, displayName: currentUser.displayName || currentUser.email },
        'leaderboard_suppress',
        '/profile/edit',
        checked ? 'Suppress' : 'Unsuppress',
        { tournamentId }
      );
    } catch (e) {
      console.error('Toggle celebration suppress:', e);
      showAlert('Failed to update setting.', { variant: 'error' });
    } finally {
      setCelebrationSuppressSaving(false);
    }
  }

  if (!currentUser) {
    navigate('/');
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const newDisplayName = (displayName || '').trim();
    const newSkillLevel = skillLevel || 'average';
    const newYears = parseInt(yearsOfExperience, 10);
    const yearsValid = !isNaN(newYears) && newYears >= 0 && newYears <= 50;
    if (!yearsValid) {
      showAlert('Years of experience must be between 0 and 50.', { variant: 'warning' });
      return;
    }
    const beforeGame = getProfileForGame(userProfile, gameId);
    const before = {
      displayName: (userProfile?.displayName || currentUser?.displayName || currentUser?.email || '').trim(),
      skillLevel: beforeGame.skillLevel || 'average',
      yearsOfExperience: beforeGame.yearsOfExperience ?? '',
      timezone: userProfile?.timezone || DEFAULT_TIMEZONE,
      ...getEntityProfileDefaults(userProfile)
    };
    const after = {
      displayName: newDisplayName,
      skillLevel: newSkillLevel,
      yearsOfExperience: newYears,
      timezone: timezone || DEFAULT_TIMEZONE,
      avatarURL: (profileFields.avatarURL || '').trim() || null,
      description: (profileFields.description || '').trim() || null,
      phone: (profileFields.phone || '').trim() || null,
      address: (profileFields.address || '').trim() || null,
      mapUrl: (profileFields.mapUrl || '').trim() || null
    };
    const changes = {};
    if (String(before.displayName) !== String(after.displayName)) {
      changes.displayName = { before: before.displayName, after: after.displayName };
    }
    if (String(before.skillLevel) !== String(after.skillLevel)) {
      changes.skillLevel = { before: before.skillLevel, after: after.skillLevel };
    }
    if (Number(before.yearsOfExperience) !== Number(after.yearsOfExperience)) {
      changes.yearsOfExperience = { before: before.yearsOfExperience, after: after.yearsOfExperience };
    }
    if (String(before.timezone || '') !== String(after.timezone || '')) {
      changes.timezone = { before: before.timezone, after: after.timezone };
    }
    ['avatarURL', 'description', 'phone', 'address', 'mapUrl'].forEach((key) => {
      if (String(before[key] || '') !== String(after[key] || '')) {
        changes[key] = { before: before[key], after: after[key] };
      }
    });
    if (Object.keys(changes).length === 0) {
      showToast('No changes to save.', { variant: 'info' });
      return;
    }
    setSaving(true);
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const existing = (await getDoc(userRef)).data() || {};
      const existingGameProfiles = existing.gameProfiles || {};
      const mergedGameProfiles = {
        ...existingGameProfiles,
        [gameId]: {
          ...(existingGameProfiles[gameId] || {}),
          skillLevel: after.skillLevel,
          yearsOfExperience: after.yearsOfExperience,
        },
      };
      await userUpdateProfile(currentUser.uid, {
        displayName: after.displayName || null,
        skillLevel: after.skillLevel,
        yearsOfExperience: after.yearsOfExperience,
        gameProfiles: mergedGameProfiles,
        avatarURL: after.avatarURL,
        description: after.description,
        phone: after.phone,
        address: after.address,
        mapUrl: after.mapUrl,
      });
      const context = {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        displayName: currentUser.displayName || currentUser.email
      };
      await logAudit(AUDIT_ACTIONS.UPDATE, AUDIT_ENTITIES.USER, currentUser.uid, context, changes, null);
      const editorName = (currentUser.displayName || currentUser.email || 'User').trim();
      await addDoc(collection(db, 'profileEditLogs'), {
        userId: currentUser.uid,
        editedBy: currentUser.uid,
        editedByName: editorName,
        editedAt: Timestamp.now(),
        changes
      });
      await refreshUserProfile();
      const membersSnap = await getDocs(query(collection(db, 'groupMembers'), where('userId', '==', currentUser.uid)));
      const approvedGroupIds = membersSnap.docs.filter((d) => d.data().approved === true).map((d) => d.data().groupId);
      await saveUserToAlgolia(
        { id: currentUser.uid, displayName: after.displayName, email: currentUser.email || '', phone: after.phone },
        approvedGroupIds
      );
      showToast('Profile updated.', { variant: 'success' });
      navigate(-1);
    } catch (err) {
      console.error('Error saving profile:', err);
      showAlert('Error saving profile: ' + (err?.message || err), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="profile-edit-page">
      <div className="profile-edit-card">
        <h1>Edit your profile</h1>
        <p className="profile-edit-note">You can update your display name, profile picture, description, phone, and skill. Role changes can only be made by a super admin.</p>
        <form onSubmit={handleSubmit}>
          <div className="profile-edit-field">
            <label htmlFor="profile-edit-displayName">Display name</label>
            <input
              id="profile-edit-displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Display name"
              className="profile-edit-input"
            />
          </div>
          <div className="profile-edit-field">
            <label htmlFor="profile-edit-skillLevel">Skill level ({gameLabel})</label>
            <select
              id="profile-edit-skillLevel"
              value={skillLevel}
              onChange={(e) => setSkillLevel(e.target.value)}
              className="profile-edit-input"
            >
              <option value="beginner">Beginner</option>
              <option value="average">Average</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
          <div className="profile-edit-field">
            <label htmlFor="profile-edit-years">Years of experience ({gameLabel})</label>
            <input
              id="profile-edit-years"
              type="number"
              min={0}
              max={50}
              value={yearsOfExperience}
              onChange={(e) => setYearsOfExperience(e.target.value)}
              placeholder="0–50"
              className="profile-edit-input"
            />
          </div>
          <div className="profile-edit-field">
            <label htmlFor="profile-edit-timezone">Timezone</label>
            <select
              id="profile-edit-timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="profile-edit-input"
              title="Dates and times will be shown in this timezone"
            >
              {TIMEZONE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <span className="profile-edit-hint">Dates and times are displayed in this timezone. Default: IST.</span>
          </div>
          <div className="profile-edit-field">
            <label>Profile details</label>
            <EntityProfileFields
              value={profileFields}
              onChange={(key, val) => setProfileFields((p) => ({ ...p, [key]: val }))}
              showAddress={true}
              showMapUrl={true}
              avatarAlt="Your avatar"
            />
          </div>
          <div className="profile-edit-actions">
            <button type="button" onClick={() => navigate(-1)} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>

      {activeTournament && (
        <div className="profile-edit-card profile-edit-card-leaderboard">
          <h2>Leaderboard notifications</h2>
          <p className="profile-edit-note">
            After login we show a celebration (confetti for top 10) or a motivation popup. You can suppress this per tournament. When your position changes in the leaderboard, the setting is automatically reset so you&apos;ll see updates.
          </p>
          <div className="profile-edit-field profile-edit-field-checkbox">
            <label className="profile-edit-checkbox-label">
              <input
                type="checkbox"
                checked={!!suppressedTournaments[activeTournament.id]}
                onChange={(e) => toggleCelebrationSuppress(activeTournament.id, e.target.checked)}
                disabled={celebrationSuppressSaving}
              />
              <span>Don&apos;t show celebrations for <strong>{activeTournament.name || 'this tournament'}</strong></span>
            </label>
            {celebrationSuppressSaving && <span className="profile-edit-hint">Saving…</span>}
          </div>
        </div>
      )}
    </div>
  );
}
